import { ApiService } from './api'

export type PartyStatusKey = 'active' | 'archived'
export type PartyListStatus = PartyStatusKey | 'all'
export type PartiesPageSize = 10 | 20 | 50 | 100

export interface PartiesPaginationDto {
	page: number
	page_size: PartiesPageSize
	total: number
	pages: number
}

export interface PartyContactPointDto {
	id: number
	contact_type_key: string
	value_raw: string
	verified_at: string | null
	created_at: string
	updated_at: string
}

export interface PartyIdentityDto {
	id: number
	module_key: string
	identity_type_key: string
	source_entity_type: string
	source_entity_id: string
	identity_key: string
	first_seen_at: string
	last_seen_at: string
}

export interface PartySummaryDto {
	id: number
	display_name: string | null
	display_label: string
	status_key: PartyStatusKey
	contact_points_count: number
	identities_count: number
	contact_points_preview: PartyContactPointDto[]
	identities_preview: PartyIdentityDto[]
	created_at: string
	updated_at: string
}

export interface PartyListDto {
	items: PartySummaryDto[]
	pagination: PartiesPaginationDto
}

export interface PartyDetailDto {
	party: PartySummaryDto
	contact_points: PartyContactPointDto[]
	identities: PartyIdentityDto[]
}

export interface PartyFiltersDto {
	module_keys: string[]
	identity_type_keys: string[]
	contact_type_keys: string[]
}

export interface PartyListQuery {
	search?: string
	status?: PartyListStatus
	module_key?: string
	identity_type_key?: string
	contact_type_key?: string
	page?: number
	page_size?: PartiesPageSize
}

export interface PartyUpdateInput {
	display_name: string | null
	expected_updated_at: string
}

export interface ContactPointCreateInput {
	contact_type_key: string
	value_raw: string
}

export interface ContactPointUpdateInput extends ContactPointCreateInput {
	expected_updated_at: string
}

export interface PartiesApiProblem {
	code: string
	message: string
	status: number
	field?: string
}

export class PartiesApiError extends Error {
	readonly code: string
	readonly status: number
	readonly field?: string

	constructor(problem: PartiesApiProblem) {
		super(problem.message)
		this.name = 'PartiesApiError'
		this.code = problem.code
		this.status = problem.status
		this.field = problem.field
	}
}

export function normalizePartiesApiError(error: unknown): PartiesApiProblem {
	if (error instanceof PartiesApiError) {
		let message = error.message || 'Не удалось выполнить запрос участников.'
		if (error.code === 'parties_concurrency_conflict')
			message = 'Данные уже изменились в другом окне. Обновите карточку и повторите действие.'
		if (error.code === 'contact_point_duplicate')
			message = 'Такой контакт этого типа уже есть у участника.'
		if (error.status === 403) message = 'Недостаточно прав для этого действия.'
		return { code: error.code, message, status: error.status, field: error.field }
	}
	if (error instanceof Error)
		return { code: 'parties_request_failed', message: error.message, status: 0 }
	return { code: 'parties_request_failed', message: 'Не удалось выполнить запрос участников.', status: 0 }
}

export class PartiesService {
	private readonly cpDomain: string

	constructor(private readonly apiService: ApiService) {
		if (!apiService) throw new Error('ApiService не инициализирован')
		this.cpDomain = apiService.getConfig().cp_domain
	}

	list(spaceId: string | number, query: PartyListQuery = {}): Promise<PartyListDto> {
		return this.request(spaceId, `/parties${this.query({ ...query })}`, 'GET')
	}

	get(spaceId: string | number, partyId: number): Promise<PartyDetailDto> {
		return this.request(spaceId, `/party/${partyId}`, 'GET')
	}

	filters(spaceId: string | number): Promise<PartyFiltersDto> {
		return this.request(spaceId, '/filters', 'GET')
	}

	createParty(spaceId: string | number, displayName: string | null = null): Promise<PartyDetailDto> {
		return this.request(spaceId, '/party', 'POST', { display_name: displayName })
	}

	updateParty(spaceId: string | number, partyId: number, input: PartyUpdateInput): Promise<PartyDetailDto> {
		return this.request(spaceId, `/party/${partyId}`, 'PATCH', input)
	}

	archiveParty(spaceId: string | number, partyId: number, expectedUpdatedAt: string): Promise<PartyDetailDto> {
		return this.request(spaceId, `/archive/${partyId}`, 'POST', { expected_updated_at: expectedUpdatedAt })
	}

	restoreParty(spaceId: string | number, partyId: number, expectedUpdatedAt: string): Promise<PartyDetailDto> {
		return this.request(spaceId, `/restore/${partyId}`, 'POST', { expected_updated_at: expectedUpdatedAt })
	}

	createContactPoint(spaceId: string | number, partyId: number, input: ContactPointCreateInput): Promise<PartyDetailDto> {
		return this.request(spaceId, `/contact-point/${partyId}`, 'POST', input)
	}

	updateContactPoint(spaceId: string | number, partyId: number, contactId: number, input: ContactPointUpdateInput): Promise<PartyDetailDto> {
		return this.request(spaceId, `/contact-point/${partyId}${this.query({ contact_point_id: contactId })}`, 'PATCH', input)
	}

	deleteContactPoint(spaceId: string | number, partyId: number, contactId: number, expectedUpdatedAt: string): Promise<PartyDetailDto> {
		return this.request(spaceId, `/contact-point/${partyId}${this.query({ contact_point_id: contactId })}`, 'DELETE', {
			expected_updated_at: expectedUpdatedAt,
		})
	}

	private async request<T>(spaceId: string | number, path: string, method: string, body?: unknown): Promise<T> {
		try {
			const response = await this.apiService.doFetch(this.cpDomain, `/${spaceId}/parties/s${path}`, method, body)
			const result = await this.apiService.getResponseJson(response)
			if (!result.success) {
				const data = result.data as { problem?: { code?: string; message?: string; field?: string } } | undefined
				const problem = data?.problem
				throw new PartiesApiError({
					code: problem?.code || 'parties_request_failed',
					message: problem?.message || String(result.error || 'Ошибка Parties API'),
					status: result.response_code ?? response.status,
					field: problem?.field,
				})
			}
			return result.data as T
		} catch (error) {
			if (error instanceof PartiesApiError) throw error
			throw new PartiesApiError(normalizePartiesApiError(error))
		}
	}

	private query(values: Record<string, string | number | undefined>): string {
		const params = new URLSearchParams()
		for (const [key, value] of Object.entries(values)) {
			if (value === undefined || value === '') continue
			params.set(key, String(value))
		}
		const query = params.toString()
		return query ? `?${query}` : ''
	}
}
